// client/src/utils/parentAverages.generated.js
//
// GENERATED - do not edit by hand.
//
// Copied from packages/core/src/academics/averages.js by tools/sync-shared.py. Edit the core file and re-run that
// script; `node tools/marks-sheet-conformance.mjs` asserts this copy has not
// drifted from it.
//
// The phone imports the original. The web cannot (packages/core is outside
// client/src, and the CRA build will not resolve it), so it gets this. They must
// stay identical - a rule that exists twice and differs is the whole class of
// bug these files were written to end.
//
// A child's reported average. The phone imported these from core and the
// web page carried its own inline copy of the same arithmetic, so the two
// could report different marks for the same child with no way for a parent
// to tell which was right. They are coefficient-weighted, like the report
// card, and the annual is withheld until every term of the year has one.

/** Mean of the finite numbers in `scores`, or null when there are none. */
export function averageFromScores(scores) {
  const list = (scores || []).map(Number).filter((n) => Number.isFinite(n));
  if (!list.length) return null;
  return list.reduce((acc, n) => acc + n, 0) / list.length;
}

/**
 * Σ(score × coefficient) / Σ(coefficient) over `[{ score, coefficient }]`.
 *
 * A coefficient of 0 excludes the mark without excluding the subject — which is
 * how the report card honours `includeInAverage: false`. Null when nothing
 * countable is left, never 0: an empty term has no average, and reporting one
 * as zero would read as a child who scored nothing.
 */
export function weightedAverage(entries) {
  let weighted = 0;
  let coefficients = 0;
  for (const entry of entries || []) {
    const score = Number(entry?.score);
    const coefficient = Number(entry?.coefficient);
    if (!Number.isFinite(score)) continue;
    if (!Number.isFinite(coefficient) || coefficient <= 0) continue;
    weighted += score * coefficient;
    coefficients += coefficient;
  }
  return coefficients > 0 ? weighted / coefficients : null;
}

/** A subject's coefficient, defaulting to 1 rather than to 0 or NaN. */
export function coefficientOf(subject) {
  const c = Number(subject?.coefficient);
  return Number.isFinite(c) && c > 0 ? c : 1;
}

/** Does this mark come from an evaluation of the given type? */
export function isType(mark, type) {
  return String(mark?.evaluationType || '').toLowerCase() === type;
}

/**
 * The distinct POSITIVE WHOLE values of `key` across `marks`, ascending.
 *
 * Positive, and that is the whole point. This used to keep anything that
 * `Number()` could make finite — and `Number(null)` is 0, not NaN. Every mark in
 * this school carries `termNumber: null`, so the filter passed them all and the
 * portal showed a "Term 0" that no school has. Sequence numbering starts at 1,
 * term numbering starts at 1; a zero here is a missing value wearing a number.
 */
export function distinctSortedNumbers(marks, key) {
  const seen = new Set();
  for (const m of marks || []) {
    const raw = m?.[key];
    if (raw == null || raw === '') continue;
    const n = Number(raw);
    if (!Number.isInteger(n) || n < 1) continue;
    seen.add(n);
  }
  return Array.from(seen).sort((a, b) => a - b);
}

/** subjectId -> coefficient, from whatever subject list the caller has. */
export function coefficientIndex(subjects) {
  const index = new Map();
  for (const s of subjects || []) {
    if (s?.id == null) continue;
    index.set(String(s.id), coefficientOf(s));
  }
  return index;
}

/**
 * Turn marks into the `{ score, coefficient }` pairs weightedAverage wants.
 * A mark the school has flagged out of the average carries coefficient 0.
 */
export function weighEntries(marks, index) {
  return (marks || []).map((m) => ({
    score: m?.score,
    coefficient: m?.includeInAverage === false
      ? 0
      : (index.has(String(m?.subjectId)) ? index.get(String(m?.subjectId)) : 1),
  }));
}

/**
 * Which term a mark belongs to.
 *
 * DERIVED FROM THE SEQUENCE when the evaluation carries no term, which in
 * practice is nearly always: every `evaluations` row in this school has a null
 * `termNumber` and a real `sequenceNumber`. Sequences 1-2 are term 1, 3-4 term
 * 2, 5-6 term 3 — the same mapping server/utils/promotionService.js already
 * applies when it decides who is promoted, so a child cannot be promoted on one
 * grouping and reported to their parent on another.
 */
export function termOf(mark, sequencesPerTerm = DEFAULT_SEQUENCES_PER_TERM) {
  const stated = Number(mark?.termNumber);
  if (Number.isInteger(stated) && stated >= 1) return stated;

  const seq = Number(mark?.sequenceNumber);
  const per = Number(sequencesPerTerm) >= 1 ? Number(sequencesPerTerm) : DEFAULT_SEQUENCES_PER_TERM;
  if (Number.isInteger(seq) && seq >= 1) return Math.ceil(seq / per);
  return null;
}

/** The sequence numbers a term is made of, e.g. term 2 -> [3, 4]. */
export function sequencesInTerm(termNumber, sequencesPerTerm = DEFAULT_SEQUENCES_PER_TERM) {
  const tn = Number(termNumber);
  const per = Number(sequencesPerTerm) >= 1 ? Number(sequencesPerTerm) : DEFAULT_SEQUENCES_PER_TERM;
  if (!Number.isInteger(tn) || tn < 1) return [];
  return Array.from({ length: per }, (_, i) => (tn - 1) * per + i + 1);
}

/**
 * Compute every average a report needs from a flat list of marks.
 *
 * @param {Array}  marks              [{ subjectId, sequenceNumber, termNumber, evaluationType, score, includeInAverage }]
 * @param {Object} [options]
 * @param {Array}  [options.subjects] [{ id, coefficient }] — omit and every coefficient is 1
 * @param {number} [options.termsPerYear=3] terms that must be present before an annual average is reported
 * @returns {{
 *   sequenceNumbers: number[],
 *   termNumbers: number[],
 *   sequenceAverages: Record<number, number|null>,
 *   termAverages: Record<number, number|null>,
 *   annualAverage: number|null,
 *   annualComplete: boolean,
 *   termsPerYear: number,
 *   termsWithAverage: number,
 *   hasAny: boolean
 * }}
 */
export function computeAverages(marks, options = {}) {
  const { subjects = null } = options;
  const termsPerYear = Number(options.termsPerYear) > 0
    ? Number(options.termsPerYear)
    : DEFAULT_TERMS_PER_YEAR;
  const sequencesPerTerm = Number(options.sequencesPerTerm) >= 1
    ? Number(options.sequencesPerTerm)
    : DEFAULT_SEQUENCES_PER_TERM;

  const index = coefficientIndex(subjects);
  const weigh = (list) => weightedAverage(weighEntries(list, index));

  const sequenceNumbers = distinctSortedNumbers(marks, 'sequenceNumber');

  const sequenceAverages = {};
  for (const sn of sequenceNumbers) {
    sequenceAverages[sn] = weigh((marks || []).filter((m) => Number(m?.sequenceNumber) === sn));
  }

  // Terms are DERIVED, not read. See termOf: the evaluations in this system
  // carry no term number, so reading the field gave every mark the same missing
  // value and the portal grouped them all under a term that does not exist.
  const termNumbers = Array.from(
    new Set((marks || []).map((m) => termOf(m, sequencesPerTerm)).filter((n) => n != null))
  ).sort((a, b) => a - b);

  // A TERM AVERAGE NEEDS ALL OF ITS SEQUENCES.
  //
  // Term 1 is sequences 1 and 2. With only sequence 1 marked, averaging it and
  // calling the result "Term 1" reports half a term as a whole one — the same
  // error as publishing an annual average from one term, one level down. The
  // term is left null until every sequence it is made of has an average.
  //
  // A term the school recorded DIRECTLY (an explicit 'term' evaluation) is its
  // own answer and is taken as given; nothing is being inferred there.
  const termAverages = {};
  const termComplete = {};
  for (const tn of termNumbers) {
    const recorded = weigh(
      (marks || []).filter((m) => termOf(m, sequencesPerTerm) === tn && isType(m, 'term'))
    );
    if (recorded != null) {
      termAverages[tn] = recorded;
      termComplete[tn] = true;
      continue;
    }

    const required = sequencesInTerm(tn, sequencesPerTerm);
    const allPresent = required.every((sn) => Number.isFinite(Number(sequenceAverages[sn])));
    termComplete[tn] = allPresent;
    termAverages[tn] = allPresent
      ? weigh((marks || []).filter((m) => termOf(m, sequencesPerTerm) === tn && isType(m, 'sequence')))
      : null;
  }

  // An annual mark the school recorded itself is the school's own answer, and a
  // recorded annual result is complete by definition.
  const recordedAnnual = weigh((marks || []).filter((m) => isType(m, 'annual')));

  // Otherwise: the mean of the term averages, and ONLY once every term of the
  // year has one. `termsWithAverage` is exported so a screen can say why the
  // figure is missing instead of just showing a dash.
  const termsWithAverage = Array.from({ length: termsPerYear }, (_, i) => i + 1)
    .filter((tn) => Number.isFinite(Number(termAverages[tn])))
    .length;
  const annualComplete = recordedAnnual != null || termsWithAverage >= termsPerYear;

  let annualAverage = null;
  if (recordedAnnual != null) {
    annualAverage = recordedAnnual;
  } else if (annualComplete) {
    annualAverage = averageFromScores(
      Array.from({ length: termsPerYear }, (_, i) => termAverages[i + 1])
    );
  }

  return {
    sequenceNumbers,
    termNumbers,
    sequenceAverages,
    termAverages,
    termComplete,
    annualAverage,
    annualComplete,
    termsPerYear,
    sequencesPerTerm,
    termsWithAverage,
    hasAny: sequenceNumbers.length > 0 || termNumbers.length > 0 || annualAverage != null,
  };
}

/**
 * Per-subject scores keyed by sequence number.
 *
 * The per-subject `average` stays an unweighted mean on purpose: one subject
 * carries one coefficient, so weighting its own sequences by it changes nothing.
 * Weighting only matters when subjects are combined.
 *
 * @returns {Array<{ id, name, code, coefficient, bySequence: Record<number, number>, average: number|null }>}
 */
export function subjectRows(subjects, marks) {
  return (subjects || []).map((s) => {
    const bySequence = {};
    for (const m of marks || []) {
      if (String(m?.subjectId) !== String(s.id)) continue;
      const sn = Number(m?.sequenceNumber);
      if (!Number.isFinite(sn)) continue;
      if (m?.score == null) continue;
      bySequence[sn] = Number(m.score);
    }
    return {
      id: s.id,
      name: s.name || s.subjectName || s.code || String(s.id),
      code: s.code || null,
      coefficient: coefficientOf(s),
      bySequence,
      average: averageFromScores(Object.values(bySequence)),
    };
  });
}

/**
 * Every subject's result for ONE sequence.
 *
 * The parent pages used to draw subjects down the side and every sequence of the
 * year across the top. That table is unreadable by the third sequence on a
 * laptop and was never readable on a phone — six numeric columns plus a subject
 * name do not fit — so the sequence is chosen and the table shows one column of
 * marks that can actually be read.
 *
 * @returns {Array<{ id, name, code, coefficient, score: number|null, weighted: number|null }>}
 */
export function subjectsForSequence(subjects, marks, sequenceNumber) {
  const sn = Number(sequenceNumber);
  return (subjects || []).map((s) => {
    const mark = (marks || []).find(
      (m) => String(m?.subjectId) === String(s.id) && Number(m?.sequenceNumber) === sn && m?.score != null
    );
    const score = mark ? Number(mark.score) : null;
    const coefficient = mark?.includeInAverage === false ? 0 : coefficientOf(s);
    return {
      id: s.id,
      name: s.name || s.subjectName || s.code || String(s.id),
      code: s.code || null,
      coefficient,
      score: Number.isFinite(score) ? score : null,
      weighted: Number.isFinite(score) ? score * coefficient : null,
    };
  });
}

/**
 * The grade and remark a score earns, from the school's own bands.
 *
 * `scale` is what the server sends on `grading.scale`: `{ grade, remark, min,
 * max }` rows read out of the school's grade_scales, sorted high to low.
 *
 * FALLS BACK TO THE SAME CBA BANDS THE REPORT CARD FALLS BACK TO
 * (calculateGradeAndRemark), so a school that has configured no scale still gets
 * the letters its report cards print rather than a blank.
 */
export function gradeFor(score, scale) {
  const n = Number(score);
  if (!Number.isFinite(n)) return null;

  for (const band of scale || []) {
    const min = Number(band?.min);
    const max = Number(band?.max);
    if (Number.isFinite(min) && Number.isFinite(max) && n >= min && n <= max) {
      return {
        grade: band.grade || band.remark || '',
        remark: band.remark || band.grade || '',
        description: band.description ?? null,
      };
    }
  }

  let grade;
  if (n >= 18) grade = 'CVWA';
  else if (n >= 15) grade = 'CWA';
  else if (n >= 12) grade = 'CA';
  else if (n >= 10) grade = 'CAA';
  else grade = 'CNA';
  return { grade, remark: grade, description: null };
}

/**
 * What the numbers actually say about a child — the Analytics tab.
 *
 * Everything here is derived from marks the portal already has: no new endpoint,
 * nothing the school has to configure, and no figure a parent could not check by
 * hand against the sequence tables above it. That constraint is deliberate. An
 * analytics panel that invents a metric nobody can reproduce is worse than an
 * empty one, because a parent cannot tell a real problem from a bug.
 *
 * `trend`       overall weighted average per sequence, in order — the shape of
 *               the year, which is the one thing a single sequence cannot show.
 * `bySubject`   each subject's own average across the year, best first, so the
 *               strongest and weakest are read off the ends rather than asserted.
 * `movement`    latest sequence minus the one before it. Null with one sequence:
 *               there is no movement yet, and 0 would read as "no progress".
 * `passing`     subjects at or above the school's pass mark in the LATEST
 *               sequence — the current standing, not the year's.
 *
 * @returns {{
 *   trend: Array<{ sequenceNumber: number, average: number }>,
 *   bySubject: Array<{ id, name, code, coefficient, average: number|null, marks: number }>,
 *   strongest: object|null,
 *   weakest: object|null,
 *   movement: number|null,
 *   latestSequence: number|null,
 *   passing: number,
 *   graded: number,
 *   hasAny: boolean
 * }}
 */
export function performanceInsights(subjects, marks, options = {}) {
  const passMark = Number(options.passMark) > 0 ? Number(options.passMark) : DEFAULT_PASS_MARK;
  const averages = computeAverages(marks, options);

  const trend = averages.sequenceNumbers
    .map((sn) => ({ sequenceNumber: sn, average: averages.sequenceAverages[sn] }))
    .filter((p) => Number.isFinite(Number(p.average)));

  const bySubject = subjectRows(subjects, marks)
    .map((row) => ({
      id: row.id,
      name: row.name,
      code: row.code,
      coefficient: row.coefficient,
      average: row.average,
      marks: Object.keys(row.bySequence).length,
    }))
    // Subjects with no mark yet sort last rather than being dropped: "not marked"
    // is information a parent wants, and hiding the row hides it.
    .sort((a, b) => {
      if (a.average == null && b.average == null) return 0;
      if (a.average == null) return 1;
      if (b.average == null) return -1;
      return b.average - a.average;
    });

  const graded = bySubject.filter((r) => r.average != null);
  const latestSequence = averages.sequenceNumbers.length
    ? averages.sequenceNumbers[averages.sequenceNumbers.length - 1]
    : null;

  const latestRows = latestSequence == null
    ? []
    : subjectsForSequence(subjects, marks, latestSequence).filter((r) => r.score != null);

  const movement = trend.length >= 2
    ? trend[trend.length - 1].average - trend[trend.length - 2].average
    : null;

  return {
    trend,
    bySubject,
    strongest: graded.length ? graded[0] : null,
    weakest: graded.length ? graded[graded.length - 1] : null,
    movement,
    latestSequence,
    passing: latestRows.filter((r) => r.score >= passMark).length,
    graded: latestRows.length,
    hasAny: trend.length > 0 || graded.length > 0,
  };
}

/**
 * Attendance rate as a percentage, or null when nothing countable exists.
 *
 * Ported from the parent dashboard, and the rules are load-bearing:
 *   * Only the most recent `window` records count. This is a "how are things
 *     going lately" figure, not a lifetime statistic — a rough patch last year
 *     should not permanently depress it.
 *   * Only present / absent / late are counted. Statuses like 'excused' or
 *     'holiday' are neither attendance nor absence, and counting them as
 *     absences would penalise a child for an authorised day off.
 *   * Late counts as ATTENDED. The figure answers "was the child in class?",
 *     which punctuality does not change.
 *
 * Shared so the phone and the web cannot report different rates for the same
 * child — see the note at the top of this file.
 */
export function attendanceRate(records, window = 60) {
  const recent = (records || []).slice(0, window);

  const considered = recent.filter((r) => {
    const s = String(r?.status || '').toLowerCase();
    return s === 'present' || s === 'absent' || s === 'late';
  });

  const attended = considered.filter((r) => {
    const s = String(r?.status || '').toLowerCase();
    return s === 'present' || s === 'late';
  }).length;

  if (!considered.length) return null;
  // One decimal place, matching the web page exactly.
  return Math.round((attended / considered.length) * 1000) / 10;
}

/** Whether a score is a pass. Null scores are not failures — they are missing. */
export function isPass(score, passMark = DEFAULT_PASS_MARK) {
  if (score == null || !Number.isFinite(Number(score))) return null;
  return Number(score) >= Number(passMark || DEFAULT_PASS_MARK);
}

/** Default pass mark when the school has not configured a grading scale. */
export const DEFAULT_PASS_MARK = 10;

/** Terms in an academic year. The report card walks 1..3 (getAnnualMarks). */
export const DEFAULT_TERMS_PER_YEAR = 3;

/** Sequences in a term. Cameroon: six sequences over three terms. */
export const DEFAULT_SEQUENCES_PER_TERM = 2;
